import { IStringLocalizer } from "./IStringLocalizer";

// See: public static class InventoryColumnIds
export enum InventoryColumnIds {
    Level = "level",
    PartName = "part-name",
    Casrn = "casrn",
    ChemicalName = "chemical-name",
    Description = "description",
    Plant = "plant",
    Alternative = "alternative",
    MaterialFamily = "material-family",
    PercentByWeight = "percent-by-weight",
    ComponentPercentByWeight = "component-percent-by-weight",
    Supplier = "supplier",
    Tags = "tags",
    Quantity = "quantity",
    SupplierPartNumber = "supplier-part-number",
    ExcludeFromCompliance = "exclude-from-compliance",
    ReachNumber = "reach-number",
    PbtOrVpvb = "pbt-or-vpvb",
    CLPClassification = "clp-classification",
    MaterialType = "material-type",
    HpdSpecialCondition = "Hpd-special-condition",
    PackageStructure = "package-structure",
    Role = "role",
    Notes = "notes",
    Status = "status",
    Proprietary = "proprietary",
    ExpireDate = "expire_date",
    Name = "name",
    UserFacingSupplierId = "user-facing-supplier-id",
    Weight = "weight",
    RecycledContent = "recycled-content",
    ProductNumber = "product-number",
    UnitWeight = "unit-weight",
    EcNumber = "ec-number",
    Nanomaterial = "nanomaterial",
    PercentPostConsumerRecycled = "percent-post-consumer-recycled-content",
    PercentPreConsumerRecycled = "percent-pre-consumer-recycled-content",
    ResidualsAndImpuritiesConsidered = "residuals-and-impurities-considered",
    ResidualsAndImpuritiesNotes = "residuals-and-impurities-notes",
    Threshold = "threshold",
    ScrapRate = "scrap-rate",
    UnitOfMeasure = "unit-of-measure",
    Url = "url",
    Yield = "yield",
    OverallRating = "overall-rating",
    RegulatoryFlag = "regulatory-flag",
    CustomFlag = "custom-flag",
    ReachRohsFlag = "reach-rohs-flag",
    C2cTechnicalOrBiologicalNutrient = "c2c-technical-or-biological-nutrient",
    C2cRecyclable = "c2c-recyclable",
    C2cBiodegradable = "c2c-biodegradable",
    C2cDisassemblability = "c2c-disassemblability",
    C2cPercentRapidlyRenewable = "c2c-percent-rapidly-renewable-content",
    C2cDescriptionRapidlyRenewable = "c2c-description-of-rapidly-renewable-content",
    C2cEmc = "c2c-emc",
    C2cNotes = "c2c-notes",
    DataSource = "data-source",
    VersionDescription = "version-description",
    ScipConcernElementMaterialCategory = "scip-concern-element-material-category",
    ScipConcernElementMixtureCategory = "scip-concern-element-mixture-category",
    WetComponentPercentByWeight = "wet-component-percent-by-weight",
    PercentWetIngredientInFinalProduct = "percent-of-wet-ingredient-in-final-product",
    WetUnitWeight = "wet-unit-weight",
    WetWeight = "wet-weight",
    WetOverallPercentByWeight = "wet-overall-percent-by-weight",
    BiodegradabilityStatus = "biodegradability-status",
    ExternalDbId = "external-db-id",
    PhysicalState = "physical-state",
    TscaAccession = "tsca-accession",
    Pmn = "pmn",
    OecdCode = "oecd-code",
    AuthorisationGranted = "authorisation-granted",
    ConfidentialBusinessInformation = "confidential-business-information",
    TotalOrganicFluorineContent = "total-organic-fluorine-content",
    EditControls = "edit-controls",
}

export default class IngredientsTableColumnManager {
    public getIngredientsColumnFriendlyNames(localizer: IStringLocalizer, extensibleColumnNames: string[]) {
        const ingredientsColumnFriendlyNames: { [columnId: string]: string } = {
            "level": localizer.localizeString("Level"),
            "part-name": localizer.localizeString("Part Name"),
            "casrn": localizer.localizeString("Chemical Registry Number"),
            "ec-number": localizer.localizeString("EC Number"),
            "chemical-name": localizer.localizeString("Chemical Name"),
            "overall-rating": localizer.localizeString("Overall Rating"),
            "regulatory-flag": localizer.localizeString("Regulatory Flag"),
            "custom-flag": localizer.localizeString("Custom Flag"),
            "reach-rohs-flag": localizer.localizeString("REACH/RoHS Flag"),
            "description": localizer.localizeString("Description"),
            "material-family": localizer.localizeString("Material Family"),
            "material-type": localizer.localizeString("Material Type"),
            "nanomaterial": localizer.localizeString("Nanomaterial"),
            "recycled-content": localizer.localizeString("Recycled Content"),
            "role": localizer.localizeString("Role"),
            "supplier": localizer.localizeString("Supplier"),
            "user-facing-supplier-id": localizer.localizeString("Supplier ID"),
            "external-db-id": localizer.localizeString("External DB ID"),
            "weight": localizer.localizeString("Weight"),
            "percent-by-weight": localizer.localizeString("Product % by Weight"),
            "component-percent-by-weight": localizer.localizeString("Material % by Weight"),
            "yield": localizer.localizeString("Yield"),
            "unit-of-measure": localizer.localizeString("Unit of Measure"),
            "notes": localizer.localizeString("Notes"),
            "plant": localizer.localizeString("Plant"),
            "residuals-and-impurities-considered": localizer.localizeString("Residuals and Impurities Evaluation Completed?"),
            "residuals-and-impurities-notes": localizer.localizeString("Residuals and Impurities Notes"),
            "threshold": localizer.localizeString("Threshold"),
            "url": localizer.localizeString("URL"),
            "alternative": localizer.localizeString("Alternative?"),
            "quantity": localizer.localizeString("Quantity"),
            "unit-weight": localizer.localizeString("Unit Weight"),
            "percent-post-consumer-recycled-content": localizer.localizeString("% Post-Consumer Recycled Content"),
            "percent-pre-consumer-recycled-content": localizer.localizeString("% Pre-Consumer Recycled Content"),
            "scrap-rate": localizer.localizeString("Scrap Rate"),
            "hpd-special-condition": localizer.localizeString("HPD Special Condition"),
            "c2c-technical-or-biological-nutrient": localizer.localizeString("Technical or Biological Nutrient"),
            "c2c-recyclable": localizer.localizeString("Recyclable?"),
            "c2c-biodegradable": localizer.localizeString("Biodegradable?"),
            "c2c-disassemblability": localizer.localizeString("Do connnections permit easy disassembly?"),
            "c2c-percent-rapidly-renewable-content": localizer.localizeString("Percent Rapidly Renewable Content"),
            "c2c-description-of-rapidly-renewable-content": localizer.localizeString("Description of Rapidly Renewable Content"),
            "c2c-emc": localizer.localizeString("Is this part intended to be an EMC?"),
            "c2c-notes": localizer.localizeString("Cradle to Cradle Notes"),
            "data-source": localizer.localizeString("Data Source"),
            "supplier-part-number": localizer.localizeString("Supplier Part #"),
            "reach-number": localizer.localizeString("REACH Registration Number"),
            "pbt-or-vpvb": localizer.localizeString("PBT or vPvB"),
            "clp-classification": localizer.localizeString("CLP Classification"),
            "version-description": localizer.localizeString("Version Description"),
            "scip-concern-element-material-category": localizer.localizeString("SCIP Concern Element Material Category"),
            "scip-concern-element-mixture-category": localizer.localizeString("SCIP Concern Element Mixture Category"),
            "wet-component-percent-by-weight": localizer.localizeString("Wet Material % by Weight"),
            "percent-of-wet-ingredient-in-final-product": localizer.localizeString("% of Wet Ingredient in Final Product"),
            "wet-unit-weight": localizer.localizeString("Wet Unit Weight"),
            "wet-weight": localizer.localizeString("Wet Weight"),
            "wet-overall-percent-by-weight": localizer.localizeString("Wet Product % by Weight"),
            "biodegradability-status": localizer.localizeString("IUCLID Biodegradability Data"),
            "proprietary": localizer.localizeString("Proprietary?"),
            "status": localizer.localizeString("Workflow Status"),
            "expire_date": localizer.localizeString("Expires"),
            "product-number": localizer.localizeString("Product Number"),
            "physical-state": localizer.localizeString("Physical State"),
            "tsca-accession": localizer.localizeString("TSCA Accession #"),
            "pmn": localizer.localizeString("PMN #"),
            "oecd-code": localizer.localizeString("OECD Industrial Function Codes"),
            "authorisation-granted": localizer.localizeString("Authorisation Granted"),
            "confidential-business-information": localizer.localizeString("Confidential Business Information"),
            "total-organic-fluorine-content": localizer.localizeString("Total Organic Fluorine Content"),
        };

        let i = 0;

        for (let extensibleColumnName of extensibleColumnNames) {
            ingredientsColumnFriendlyNames[`extensible-column-${i}`] = extensibleColumnName;

            ++i
        }

        return ingredientsColumnFriendlyNames;
    }
}
